import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import toast from 'react-hot-toast';
import { useNdr } from '../hooks/useNdr';
import { useAuth } from '../hooks/useAuth';
import { X } from 'lucide-react';

interface NdrActionModalProps {
  ndr: any;
  onClose: () => void;
  onSuccess: () => void;
  initialAction?: string;
}

const ACTION_OPTIONS = [
  { id: 'REATTEMPT', label: 'Re-attempt needed' },
  { id: 'UPDATE_CONSIGNEE_DETAILS', label: 'Update consignee details' },
  { id: 'FUTURE_DELIVERY', label: 'Future Delivery' },
  { id: 'SELF_COLLECT', label: 'Consignee Self collect' },
];

export const NdrActionModal: React.FC<NdrActionModalProps> = ({
  ndr,
  onClose,
  onSuccess,
  initialAction = 'REATTEMPT',
}) => {
  const { updateNdr, loading } = useNdr();
  const { user } = useAuth();
  const [action, setAction] = useState(initialAction);
  const [comment, setComment] = useState('');

  // Sub-form states
  const [consigneeName, setConsigneeName] = useState(
    ndr.delivery_address?.contact_name || ''
  );
  const [consigneeAddress1, setConsigneeAddress1] = useState(
    ndr.delivery_address?.address1 || ''
  );
  const isDummy = (val?: string | null) => {
    if (!val || typeof val !== 'string') return true;
    const lower = val.trim().toLowerCase();
    return (
      lower.includes('not available') ||
      lower.includes('not applicable') ||
      lower === 'n/a' ||
      lower === 'na' ||
      lower === 'null' ||
      lower === 'none'
    );
  };

  const [consigneeAddress2, setConsigneeAddress2] = useState(
    isDummy(ndr.delivery_address?.address2) ? '' : ndr.delivery_address?.address2 || ''
  );
  const [consigneeCity, setConsigneeCity] = useState(
    ndr.delivery_address?.city || ''
  );
  const [consigneeState, setConsigneeState] = useState(
    ndr.delivery_address?.state || ''
  );
  const [consigneePincode, setConsigneePincode] = useState(
    ndr.delivery_address?.pincode || ''
  );
  const [consigneeLandmark, setConsigneeLandmark] = useState(
    ndr.delivery_address?.landmark || ''
  );
  const [consigneeContact, setConsigneeContact] = useState(
    ndr.delivery_address?.contact_no || ''
  );
  const [deliveryDate, setDeliveryDate] = useState('');

  const handleSubmit = async () => {
    try {
      const payload: any = {
        tracking_number: ndr.tracking_number,
        courier_id: ndr.courier_id,
        courier_remark: ndr.courier_remark,
        action: action,
        additional_comment: comment,
        delivery_attempt_count: ndr.delivery_attempt_count,
        is_fake: 0,
      };

      if (action === 'UPDATE_CONSIGNEE_DETAILS') {
        payload.updated_consignee_name = consigneeName;
        // Combining back for the payload if needed, or sending individually if the API supports it.
        // User's provided sample shows 'updated_consignee_address' as a string.
        payload.updated_consignee_address = [
          consigneeAddress1,
          consigneeAddress2,
          consigneeLandmark ? `Landmark: ${consigneeLandmark}` : '',
          consigneeCity,
          consigneeState,
          consigneePincode,
        ]
          .filter(Boolean)
          .join(', ');
        payload.updated_consignee_contact_no = consigneeContact;
      } else if (action === 'FUTURE_DELIVERY') {
        if (!deliveryDate) {
          toast.error('Please select a delivery date');
          return;
        }
        payload.updated_delivery_date = new Date(deliveryDate).getTime();
      }

      await updateNdr(user?.id as any, payload);
      toast.success('NDR updated successfully');
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update NDR');
    }
  };

  return (
    <div className="p-0 flex flex-col h-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
      <div className="flex-1 overflow-y-auto p-6 space-y-8">
        {/* Main Action Selection */}
        <div className="space-y-4">
          <p className="text-sm font-semibold text-gray-400  tracking-widest">
            Select Action
          </p>
          <div className="grid grid-cols-1 gap-3">
            {ACTION_OPTIONS.map((opt) => (
              <label
                key={opt.id}
                className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all cursor-pointer hover:shadow-md ${
                  action === opt.id
                    ? 'border-teal bg-teal/5 shadow-sm'
                    : 'border-gray-100 bg-white'
                }`}
              >
                <input
                  type="radio"
                  name="ndrAction"
                  value={opt.id}
                  checked={action === opt.id}
                  onChange={(e) => setAction(e.target.value)}
                  className="w-5 h-5 text-teal border-gray-300 focus:ring-teal cursor-pointer"
                />
                <span
                  className={`font-semibold ${action === opt.id ? 'text-teal' : 'text-gray-700'}`}
                >
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Conditional Sub-forms */}
        {action === 'UPDATE_CONSIGNEE_DETAILS' && (
          <div className="space-y-5 animate-in slide-in-from-top-4 duration-300 p-5 bg-blue-50/30 rounded-2xl border border-blue-100">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-6 bg-blue-500 rounded-full" />
              <h3 className="text-lg font-bold text-blue-900">
                Update Consignee Details
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-sm font-medium text-blue-800 ml-1">Name</p>
                <Input
                  value={consigneeName}
                  onChange={(e) => setConsigneeName(e.target.value)}
                  placeholder="Full Name"
                  className="bg-white border-blue-200 focus:border-blue-500"
                />
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-blue-800 ml-1">Mobile</p>
                <Input
                  value={consigneeContact}
                  onChange={(e) => setConsigneeContact(e.target.value)}
                  placeholder="Contact Number"
                  className="bg-white border-blue-200 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-blue-800 ml-1">
                    Address Line 1
                  </p>
                  <Input
                    value={consigneeAddress1}
                    onChange={(e) => setConsigneeAddress1(e.target.value)}
                    placeholder="House No, Building, Street"
                    className="bg-white border-blue-200 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-blue-800 ml-1">
                    Address Line 2
                  </p>
                  <Input
                    value={consigneeAddress2}
                    onChange={(e) => setConsigneeAddress2(e.target.value)}
                    placeholder="Area, Colony, Sector"
                    className="bg-white border-blue-200 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-blue-800 ml-1">
                    Landmark
                  </p>
                  <Input
                    value={consigneeLandmark}
                    onChange={(e) => setConsigneeLandmark(e.target.value)}
                    placeholder="Near by..."
                    className="bg-white border-blue-200 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-blue-800 ml-1">
                    Pincode
                  </p>
                  <Input
                    value={consigneePincode}
                    onChange={(e) => setConsigneePincode(e.target.value)}
                    placeholder="6-digit Pincode"
                    className="bg-white border-blue-200 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-blue-800 ml-1">City</p>
                  <Input
                    value={consigneeCity}
                    onChange={(e) => setConsigneeCity(e.target.value)}
                    placeholder="City"
                    className="bg-white border-blue-200 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-blue-800 ml-1">
                    State
                  </p>
                  <Input
                    value={consigneeState}
                    onChange={(e) => setConsigneeState(e.target.value)}
                    placeholder="State"
                    className="bg-white border-blue-200 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {action === 'FUTURE_DELIVERY' && (
          <div className="space-y-4 animate-in slide-in-from-top-4 duration-300 p-5 bg-orange-50/30 rounded-2xl border border-orange-100">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-6 bg-orange-500 rounded-full" />
              <h3 className="text-lg font-bold text-orange-900">
                Schedule Delivery
              </h3>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-orange-800 ml-1">
                Select New Date
              </p>
              <Input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                className="bg-white border-orange-200 focus:border-orange-500"
              />
            </div>
          </div>
        )}

        {/* Common Comments */}
        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-400  tracking-widest ml-1">
            Additional Comment
          </p>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Type your notes here..."
            className="w-full h-32 p-4 rounded-xl border border-gray-200 focus:border-teal outline-none transition-all bg-white text-sm"
          />
        </div>
      </div>

      {/* Footer */}
      <div className="p-6  flex items-center justify-end gap-3">
        <Button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          disabled={loading}
          className="px-10 font-bold bg-teal text-white hover:bg-teal-dark shadow-lg shadow-teal/20"
        >
          {loading ? 'SAVING...' : 'SAVE'}
        </Button>
      </div>
    </div>
  );
};

export default NdrActionModal;
